import { IWizard } from '@/modules/admin/workflows/types/wizard-types'
import React, { createContext, useContext, useEffect, useReducer } from 'react'

/* eslint-disable react-refresh/only-export-components -- context module */

export interface CartItem {
    id: string
    name: string
    price: string
    quantity: number
    image: string
    category: string
    subcategory?: string
    description?: string
    slug: string
    uses?: number
    wizardId: string
    wizards?: IWizard[]
    type: string
    bundleId?: string
}

interface CartState {
    items: CartItem[]
    total: number
    bundleId?: string
}

type CartAction =
    | { type: 'ADD_ITEM'; payload: CartItem }
    | { type: 'REMOVE_ITEM'; payload: string }
    | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
    | { type: 'CLEAR_CART' }
    | { type: 'SET_CART'; payload: CartState }

const CartContext = createContext<{
    state: CartState
    dispatch: React.Dispatch<CartAction>
} | null>(null)

const parsePrice = (priceString: string): number => {
    try {
        const sanitizedPrice = priceString.replace(/[^0-9.]/g, '')
        const price = parseFloat(sanitizedPrice)
        return isNaN(price) ? 0 : Math.round(price * 100) / 100
    } catch (error) {
        console.error('Error parsing price:', error)
        return 0
    }
}

const calculateCartTotal = (items: CartItem[]): number => {
    return items.reduce((sum, item) => {
        const itemPrice = parsePrice(item.price)
        return sum + itemPrice * item.quantity
    }, 0)
}

const cartReducer = (state: CartState, action: CartAction): CartState => {
    switch (action.type) {
        case 'ADD_ITEM': {
            const existingItem = state.items.find(item => item.id === action.payload.id)
            if (existingItem) {
                const updatedItems = state.items.map(item =>
                    item.id === action.payload.id ? { ...item, quantity: item.quantity + 1 } : item
                )
                return {
                    ...state,
                    items: updatedItems,
                    total: calculateCartTotal(updatedItems),
                    bundleId: action.payload.bundleId || state.bundleId,
                }
            }
            const newItems = [...state.items, { ...action.payload, quantity: 1 }]
            return {
                ...state,
                items: newItems,
                total: calculateCartTotal(newItems),
                bundleId: action.payload.bundleId || state.bundleId,
            }
        }
        case 'REMOVE_ITEM': {
            const updatedItems = state.items.filter(item => item.id !== action.payload)
            return {
                ...state,
                items: updatedItems,
                total: calculateCartTotal(updatedItems),
                bundleId: updatedItems.some(item => item.bundleId === state.bundleId) ? state.bundleId : undefined,
            }
        }
        case 'UPDATE_QUANTITY': {
            if (action.payload.quantity <= 0) {
                return cartReducer(state, { type: 'REMOVE_ITEM', payload: action.payload.id })
            }

            const updatedItems = state.items.map(item =>
                item.id === action.payload.id ? { ...item, quantity: action.payload.quantity } : item
            )
            return {
                ...state,
                items: updatedItems,
                total: calculateCartTotal(updatedItems),
            }
        }
        case 'CLEAR_CART':
            return {
                items: [],
                total: 0,
                bundleId: undefined,
            }
        case 'SET_CART':
            return action.payload
        default:
            return state
    }
}

const initialCartState: CartState = { items: [], total: 0 }

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
    const [state, dispatch] = useReducer(cartReducer, initialCartState)

    useEffect(() => {
        const loadCart = () => {
            try {
                const savedCart = localStorage.getItem('cart')
                if (savedCart) {
                    const parsedCart = JSON.parse(savedCart) as CartState

                    if (parsedCart && Array.isArray(parsedCart.items)) {
                        const validCart = {
                            ...parsedCart,
                            total: calculateCartTotal(parsedCart.items),
                        }
                        dispatch({ type: 'SET_CART', payload: validCart })
                    }
                }
            } catch (error) {
                console.error('Error loading cart from localStorage:', error)
                localStorage.removeItem('cart')
            }
        }

        loadCart()
    }, [])

    useEffect(() => {
        if (state.items.length > 0 || state.bundleId) {
            localStorage.setItem('cart', JSON.stringify(state))
        } else {
            localStorage.removeItem('cart')
        }
    }, [state])

    return <CartContext.Provider value={{ state, dispatch }}>{children}</CartContext.Provider>
}

export const useCart = () => {
    const context = useContext(CartContext)
    if (!context) {
        throw new Error('useCart must be used within a CartProvider')
    }
    return context
}
